<div class="p-4">
    <jjames-card>
        <div class="flex items-center justify-between mb-6">
            <div class="flex items-center gap-4">
                <button mat-icon-button (click)="goBack()">
                    <mat-icon>arrow_back</mat-icon>
                </button>
                <div>
                    <h2 class="text-xl font-semibold">Camera Detections</h2>
                    @if (camera()) {
                    <p class="text-gray-500">
                        Camera: {{ camera()?.deviceName }} ({{ camera()?.id }})
                    </p>
                    }
                </div>
            </div>
            <jjames-button variant="secondary" (click)="loadDetections()" [disabled]="isLoading()">
                <mat-icon [class.animate-spin]="isLoading()">refresh</mat-icon>
                Refresh
            </jjames-button>
        </div>

        <!-- Filters Section -->
        <div class="mb-6 p-4 bg-gray-50 rounded border">
            <h3 class="text-sm font-semibold mb-4">Filters</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <!-- Plate Number Filter -->
                <div class="flex flex-col">
                    <label class="text-xs font-medium text-gray-700 mb-1">Plate Number</label>
                    <input type="text" placeholder="Enter plate number" [ngModel]="plateNumber()"
                        (ngModelChange)="plateNumber.set($event)"
                        class="px-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                </div>

                <!-- Direction Filter -->
                <div class="flex flex-col">
                    <label class="text-xs font-medium text-gray-700 mb-1">Direction</label>
                    <select [ngModel]="direction()" (ngModelChange)="direction.set($event)"
                        class="px-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                        <option [value]="undefined">All</option>
                        <option value="Approach">Approach</option>
                        <option value="Departure">Departure</option>
                        <option value="Both">Both</option>
                        <option value="Unknown">Unknown</option>
                    </select>
                </div>

                <!-- Start Date Filter -->
                <div class="flex flex-col">
                    <label class="text-xs font-medium text-gray-700 mb-1">Start Date</label>
                    <input type="datetime-local" [ngModel]="startDate()" (ngModelChange)="startDate.set($event)"
                        class="px-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                </div>

                <!-- End Date Filter -->
                <div class="flex flex-col">
                    <label class="text-xs font-medium text-gray-700 mb-1">End Date</label>
                    <input type="datetime-local" [ngModel]="endDate()" (ngModelChange)="endDate.set($event)"
                        class="px-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                </div>
            </div>

            <div class="mt-4 flex gap-2">
                <jjames-button variant="primary" [iconLeft]="'search'" (click)="loadDetections()"
                    [disabled]="isLoading()">

                    Apply Filters
                </jjames-button>
                <jjames-button variant="secondary" [iconLeft]="'clear'" (click)="clearFilters()"
                    [disabled]="isLoading()">
                    Clear Filters
                </jjames-button>
            </div>
        </div>

        <div class="relative overflow-hidden">
            @if (isLoading()) {
            <div class="absolute inset-0 z-10 flex items-center justify-center bg-white/50">
                <mat-spinner diameter="40"></mat-spinner>
            </div>
            }

            <table mat-table [dataSource]="dataSource" multiTemplateDataRows class="w-full">
                <!-- Detection Time -->
                <ng-container matColumnDef="detectionTime">
                    <th mat-header-cell *matHeaderCellDef>Time</th>
                    <td mat-cell *matCellDef="let row">
                        {{ row.detectionTime | date:'medium' }}
                    </td>
                </ng-container>

                <!-- Plate Number -->
                <ng-container matColumnDef="plateNumber">
                    <th mat-header-cell *matHeaderCellDef>Plate Number</th>
                    <td mat-cell *matCellDef="let row">
                        <span class="font-mono bg-gray-100 px-2 py-1 rounded border">
                            {{ row.plateNumber }}
                        </span>
                    </td>
                </ng-container>

                <!-- Confidence -->
                <ng-container matColumnDef="confidence">
                    <th mat-header-cell *matHeaderCellDef>Confidence</th>
                    <td mat-cell *matCellDef="let row">
                        {{ row.confidence ? (row.confidence * 100).toFixed(1) + '%' : 'N/A' }}
                    </td>
                </ng-container>

                <!-- Direction -->
                <ng-container matColumnDef="direction">
                    <th mat-header-cell *matHeaderCellDef>Direction</th>
                    <td mat-cell *matCellDef="let row">
                        <span class="px-2 py-1 rounded text-xs font-medium"
                            [ngClass]="row.direction === 'In' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'">
                            {{ row.direction }}
                        </span>
                    </td>
                </ng-container>

                <!-- Actions -->
                <ng-container matColumnDef="actions">
                    <th mat-header-cell *matHeaderCellDef>Actions</th>
                    <td mat-cell *matCellDef="let row">
                        @if (row.vehicleImageUrl || row.plateImageUrl) {
                        <button mat-button color="primary" (click)="toggleImage(row.id); $event.stopPropagation()">
                            <mat-icon>{{ showImages[row.id] ? 'visibility_off' : 'visibility' }}</mat-icon>
                            {{ showImages[row.id] ? 'Hide Image' : 'Show Image' }}
                        </button>
                        } @else {
                        <span class="text-gray-400 italic px-4">Empty</span>
                        }
                    </td>
                </ng-container>

                <!-- Expanded Content Column - The image -->
                <ng-container matColumnDef="expandedDetail">
                    <td mat-cell *matCellDef="let row" [attr.colspan]="displayedColumns.length" class="p-0 border-none">
                        <div class="overflow-hidden flex transition-all duration-300"
                            [style.height]="showImages[row.id] ? 'auto' : '0'"
                            [style.opacity]="showImages[row.id] ? '1' : '0'">
                            <div class="bg-gray-50 border-t p-4 w-full">
                                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div class="flex flex-col">
                                        <h4 class="text-sm font-semibold mb-2 flex items-center gap-2">
                                            <mat-icon class="text-gray-500">directions_car</mat-icon>
                                            Vehicle Image
                                        </h4>
                                        @if (row.vehicleImageUrl) {
                                        <img [src]="row.vehicleImageUrl"
                                            class="max-w-full h-auto rounded shadow-sm border bg-white"
                                            alt="Vehicle image" (error)="onImageError($event)">
                                        } @else {
                                        <div
                                            class="bg-white rounded border border-dashed p-8 flex flex-col items-center justify-center text-gray-400">
                                            <mat-icon class="text-4xl mb-2">image_not_supported</mat-icon>
                                            <span>Empty</span>
                                        </div>
                                        }
                                    </div>
                                    <div class="flex flex-col">
                                        <h4 class="text-sm font-semibold mb-2 flex items-center gap-2">
                                            <mat-icon class="text-gray-500">wysiwyg</mat-icon>
                                            Plate Image
                                        </h4>
                                        @if (row.plateImageUrl) {
                                        <div
                                            class="bg-white p-4 rounded border shadow-sm flex items-center justify-center">
                                            <img [src]="row.plateImageUrl" class="max-h-32 rounded" alt="Plate image"
                                                (error)="onImageError($event)">
                                        </div>
                                        } @else {
                                        <div
                                            class="bg-white rounded border border-dashed p-8 flex flex-col items-center justify-center text-gray-400">
                                            <mat-icon class="text-4xl mb-2">image_not_supported</mat-icon>
                                            <span>Empty</span>
                                        </div>
                                        }
                                    </div>
                                </div>
                            </div>

                        </div>

                    </td>
                </ng-container>

                <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
                <tr mat-row *matRowDef="let row; columns: displayedColumns;"
                    [class.cursor-pointer]="row.vehicleImageUrl || row.plateImageUrl"
                    class="hover:bg-gray-50 transition-colors"
                    (click)="(row.vehicleImageUrl || row.plateImageUrl) && toggleImage(row.id)"></tr>
                <tr mat-row *matRowDef="let row; columns: ['expandedDetail']" class="h-0 border-none"></tr>
            </table>

            @if (dataSource.data.length === 0 && !isLoading()) {
            <div class="py-12 text-center text-gray-500 border-x border-b">
                No detections found for this camera.
            </div>
            }

            <mat-paginator [length]="totalCount()" [pageSize]="pageSize()" [pageIndex]="pageIndex()"
                [pageSizeOptions]="[10, 25, 50, 100]" (page)="onPageChange($event)" showFirstLastButtons
                class="border-x border-b rounded-b">
            </mat-paginator>
        </div>
    </jjames-card>
</div>